import { Badge, Colors, Icons, SectionHeader, Notification, Tabs } from '@replai-platform/ui-components';
import { useMemo, useState } from 'react';
import * as SDK from '@replai-platform/sdk';
import { useFeature } from '@optimizely/react-sdk';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import TopNavPageTitle from '../../components/TopNavPageTitle';
import { RootState } from '../../store/rootReducer';
import { Page } from '../../utils/enums';
import { CommonSnackbar } from '../../utils/styles';
import { GeneratingReportNotificationStatus, ReportsActions } from '../../store/reports';
import AllReportsTab from './Tabs/AllReportsTab';
import FavoriteReportsTab from './Tabs/FavoriteReportsTab';
import TemplatesTab from './Tabs/TemplatesTab';

const GenerateReportContainer = styled.div`
  margin-top: 2rem;
`;

// Same compact tiles as the Upload Videos sources, but each available format is a button that opens its builder
const ReportTypesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  gap: 0.75rem;
  margin: 0 0 2rem 0;
`;

const ReportTypeTile = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid ${Colors.Gray[200]};
  border-radius: 0.75rem;
  background-color: ${Colors.Common.White};
  color: ${Colors.Gray[900]};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 150ms ease-out, background-color 150ms ease-out;

  &:hover:not(:disabled) {
    border-color: ${Colors.Primary[300]};
    background-color: ${Colors.Primary[25]};
  }

  &:focus-visible {
    outline: none;
    border-color: ${Colors.Primary[600]};
    box-shadow: 0 0 0 4px ${Colors.Primary[100]};
  }

  &:disabled {
    background-color: ${Colors.Gray[50]};
    color: ${Colors.Gray[500]};
    cursor: default;
  }
`;

const ReportTypeLogo = styled.img<{ $disabled: boolean }>`
  width: 2rem;
  height: 2rem;
  object-fit: contain;
  ${({ $disabled }) => ($disabled ? 'filter: grayscale(100%); opacity: 0.5;' : '')}
`;

const ReportTypeName = styled.span`
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
`;

const ChevronRightIcon = Icons.getBaseIcon('ChevronRight');

const REPORT_TYPES = (isTechmerc1575Enabled: boolean) => [
  {
    type: SDK.ReportTypes.GOOGLE_SLIDES,
    title: 'Google Slides',
    route: 'google-slides',
    enabled: true,
    image: SDK.getReportTypeImageUrl(SDK.ReportTypes.GOOGLE_SLIDES),
  },
  {
    type: SDK.ReportTypes.GOOGLE_SHEETS,
    title: 'Google Sheets',
    route: 'google-sheets',
    enabled: isTechmerc1575Enabled,
    image: SDK.getReportTypeImageUrl(SDK.ReportTypes.GOOGLE_SHEETS),
  },
  {
    type: SDK.ReportTypes.LOOKER,
    title: 'Google Looker',
    enabled: false,
    image: SDK.getReportTypeImageUrl(SDK.ReportTypes.LOOKER),
  },
  {
    type: SDK.ReportTypes.TABLEAU,
    title: 'Tableau',
    enabled: false,
    image: SDK.getReportTypeImageUrl(SDK.ReportTypes.TABLEAU),
  },
  {
    type: SDK.ReportTypes.SLACK,
    title: 'Slack Alert',
    enabled: false,
    image: SDK.getReportTypeImageUrl(SDK.ReportTypes.SLACK),
  },
];

enum AvailableTabs {
  ALL_REPORTS = 'all_reports',
  FAVORITES = 'favorites',
  TEMPLATES = 'templates',
}

const Reports = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const projectId = useSelector((state: RootState) => state.project.id);
  const generatingReportStatus = useSelector((state: RootState) => state.reports.generatingReportNotification);
  const isGeneratingReportSuccess = generatingReportStatus === GeneratingReportNotificationStatus.SUCCESS;
  const [isTechmerc1575Enabled] = useFeature('techmerc-1575'); // Google Sheets export

  const [currentTab, setCurrentTab] = useState<AvailableTabs>(AvailableTabs.ALL_REPORTS);

  const renderedTab = useMemo(() => {
    switch (currentTab) {
      case AvailableTabs.ALL_REPORTS:
        return <AllReportsTab />;
      case AvailableTabs.FAVORITES:
        return <FavoriteReportsTab />;
      case AvailableTabs.TEMPLATES:
        return <TemplatesTab />;
      default:
        return null;
    }
  }, [currentTab]);

  return (
    <>
      <TopNavPageTitle
        title="Reports"
        subtitle="Effortlessly transform your data into beautifully tailored reports"
        eventPrefix="reports"
      />
      <GenerateReportContainer>
        <SectionHeader
          title="Generate a new Report"
          subtitle="Choose one of our available formats"
          withDivider={false}
        />
        <ReportTypesContainer>
          {REPORT_TYPES(isTechmerc1575Enabled).map((reportType) => (
            <ReportTypeTile
              key={reportType.type}
              type="button"
              disabled={!reportType.enabled}
              onClick={() => {
                dispatch(ReportsActions.reset());
                navigate(`/${projectId}/${Page.Reports}/${reportType.route ?? ''}`);
              }}
            >
              <ReportTypeLogo src={reportType.image} alt="" $disabled={!reportType.enabled} />
              <ReportTypeName>{reportType.title}</ReportTypeName>
              {reportType.enabled ? (
                <ChevronRightIcon color={Colors.Gray[400]} dimension={20} />
              ) : (
                <Badge size="sm" color="Gray">
                  Not available
                </Badge>
              )}
            </ReportTypeTile>
          ))}
        </ReportTypesContainer>
      </GenerateReportContainer>
      <SectionHeader title="Your reports" subtitle="See your previously generated reports" withDivider={false} />
      <Tabs
        tabLabels={[
          { id: AvailableTabs.ALL_REPORTS, label: 'All reports' },
          { id: AvailableTabs.FAVORITES, label: 'Favorites' },
          { id: AvailableTabs.TEMPLATES, label: 'Templates' },
        ]}
        onTabChange={(tab) => {
          setCurrentTab((tab?.id ?? AvailableTabs.ALL_REPORTS) as AvailableTabs);
        }}
      >
        {renderedTab ?? <div />}
      </Tabs>

      <CommonSnackbar
        open={generatingReportStatus && generatingReportStatus !== GeneratingReportNotificationStatus.TODO}
        onClose={() => {
          dispatch(ReportsActions.setGeneratingReportNotification(GeneratingReportNotificationStatus.TODO));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={3000}
      >
        <Notification
          badgeTitle={isGeneratingReportSuccess ? 'Success' : 'Failure'}
          badgeLabel={
            isGeneratingReportSuccess ? 'Your report is being generated' : 'Your report failed to be generated'
          }
          message={
            isGeneratingReportSuccess
              ? 'In about 5 minutes, you will receive it by email'
              : 'Try again or contact your account manager'
          }
          color={isGeneratingReportSuccess ? 'Success' : 'Error'}
        />
      </CommonSnackbar>
    </>
  );
};

export default Reports;
